import { motion, AnimatePresence } from 'framer-motion'
import { X, BarChart3, PieChart, LineChart, TrendingUp, Calendar, DollarSign, Sparkles } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface ChartsDetailsModalProps {
  isOpen: boolean
  onClose: () => void
}

const ChartsDetailsModal = ({ isOpen, onClose }: ChartsDetailsModalProps) => {
  const { t } = useLanguage()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
            >
              <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-b border-white/10 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                      <BarChart3 className="h-8 w-8 text-purple-400" />
                      {t('charts.modal.title')}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">{t('charts.modal.subtitle')}</p>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto px-6 py-8 max-h-[calc(90vh-100px)]">
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-purple-500/20 rounded-xl">
                        <Sparkles className="h-6 w-6 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{t('charts.modal.whatIs')}</h3>
                        <p className="text-gray-300 leading-relaxed">{t('charts.modal.intro')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('charts.modal.availableCharts')}</h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white/5 rounded-2xl p-6 border border-purple-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                          <PieChart className="h-6 w-6 text-purple-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('charts.modal.pie.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">{t('charts.modal.pie.description')}</p>
                      <div className="bg-purple-500/10 rounded-lg p-3">
                        <p className="text-xs text-purple-300">{t('charts.modal.pie.example')}</p>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/5 rounded-2xl p-6 border border-pink-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                          <LineChart className="h-6 w-6 text-pink-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('charts.modal.line.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">{t('charts.modal.line.description')}</p>
                      <div className="bg-pink-500/10 rounded-lg p-3">
                        <p className="text-xs text-pink-300">{t('charts.modal.line.example')}</p>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="bg-white/5 rounded-2xl p-6 border border-blue-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                          <BarChart3 className="h-6 w-6 text-blue-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('charts.modal.bar.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">{t('charts.modal.bar.description')}</p>
                      <div className="bg-blue-500/10 rounded-lg p-3">
                        <p className="text-xs text-blue-300">{t('charts.modal.bar.example')}</p>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-white/5 rounded-2xl p-6 border border-green-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                          <TrendingUp className="h-6 w-6 text-green-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('charts.modal.trend.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">{t('charts.modal.trend.description')}</p>
                      <div className="bg-green-500/10 rounded-lg p-3">
                        <p className="text-xs text-green-300">{t('charts.modal.trend.example')}</p>
                      </div>
                    </motion.div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('charts.modal.insights')}</h3>

                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      { icon: Calendar, title: t('charts.modal.insight1.title'), desc: t('charts.modal.insight1.description'), color: 'purple' },
                      { icon: DollarSign, title: t('charts.modal.insight2.title'), desc: t('charts.modal.insight2.description'), color: 'pink' },
                      { icon: TrendingUp, title: t('charts.modal.insight3.title'), desc: t('charts.modal.insight3.description'), color: 'blue' },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className={`bg-gradient-to-br from-${item.color}-500/20 to-${item.color}-600/20 rounded-2xl p-6 border border-${item.color}-500/30`}
                      >
                        <div className={`w-12 h-12 bg-${item.color}-500/30 rounded-xl flex items-center justify-center mb-4`}>
                          <item.icon className={`h-6 w-6 text-${item.color}-400`} />
                        </div>
                        <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                        <p className="text-sm text-gray-300">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('charts.modal.customization')}</h3>
                  
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl p-6 border border-purple-500/30">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-purple-300 text-xs font-bold">1</span>
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{t('charts.modal.custom1.title')}</h4>
                          <p className="text-gray-300 text-sm">{t('charts.modal.custom1.description')}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-purple-300 text-xs font-bold">2</span>
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{t('charts.modal.custom2.title')}</h4>
                          <p className="text-gray-300 text-sm">{t('charts.modal.custom2.description')}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-purple-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-purple-300 text-xs font-bold">3</span>
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{t('charts.modal.custom3.title')}</h4>
                          <p className="text-gray-300 text-sm">{t('charts.modal.custom3.description')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-8 border border-purple-500/30 text-center">
                  <h3 className="text-2xl font-bold text-white mb-3">{t('charts.modal.cta')}</h3>
                  <p className="text-gray-300 mb-6">{t('charts.modal.ctaDescription')}</p>
                  <button
                    onClick={onClose}
                    className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl transition-all"
                  >
                    {t('charts.modal.gotIt')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default ChartsDetailsModal
